import type { Cell, RowData, StockFeatures } from '@tanstack/react-table';
import { createContext, type PropsWithChildren } from 'react';

/**
 * Context for the subrow indicator.
 * Since it is not an individual column, we cannot use the cellDeps to invalidate
 * the memo of the rendered cell, and we need to move the change through the
 * conditional context.
 * @internal
 */
export const SubrowCellContext = createContext<{
  isSubRowIndicatorColumn?: boolean;
  isExpanded?: boolean;
  canExpand?: boolean;
  canExpandDisabled?: boolean;
}>({});

/**
 * ContextProvider for the subrow indicator.
 * Since it is not an individual column, we cannot use the cellDeps to invalidate
 * the memo of the rendered cell and we need to move the change through the
 * conditional context.
 * @internal
 */
export function SubRowCellContextProvider<TData extends RowData, TValue>(
  props: PropsWithChildren<{
    isSubRowIndicatorColumn: boolean;
    cell: Cell<StockFeatures, TData, TValue>;
  }>,
) {
  if (props.isSubRowIndicatorColumn) {
    return (
      <SubrowCellContext.Provider
        value={{
          isSubRowIndicatorColumn: props.isSubRowIndicatorColumn,
          isExpanded: props.cell.row.getIsExpanded(),
          canExpand: props.cell.row.getCanExpand(),
          canExpandDisabled: props.cell.row.getCanExpandDisabled(),
        }}
      >
        {props.children}
      </SubrowCellContext.Provider>
    );
  }
  return props.children;
}
